// src/components/charts/MiniCharts.jsx
// Small inline SVG charts for KPI tiles: ring, half gauge, spark bars,
// sparkline, mini histogram, streak runs and a bar list. Each mark has a
// hover title with the numbers; colours come from the design tokens.

import React from "react";
import { pct } from "../../ui/format";
import { useWidth } from "./useWidth";

const WIN = "var(--win)";
const LOSS = "var(--loss)";
const TRACK = "rgba(255,255,255,0.07)";

const polar = (cx, cy, r, deg) => {
  const a = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};
const arc = (cx, cy, r, from, to) => {
  const [x1, y1] = polar(cx, cy, r, from);
  const [x2, y2] = polar(cx, cy, r, to);
  return `M ${x1} ${y1} A ${r} ${r} 0 ${to - from > 180 ? 1 : 0} 1 ${x2} ${y2}`;
};

/** Donut of wins vs losses. */
export function Ring({ wins = 0, losses = 0, size = 64, thickness = 8, center }) {
  const n = wins + losses;
  const r = (size - thickness) / 2;
  const c = size / 2;
  const deg = n ? (wins / n) * 360 : 0;
  const gap = n && wins && losses ? 3 : 0;
  return (
    <svg width={size} height={size} role="img" aria-label={`ชนะ ${wins} แพ้ ${losses}`}>
      <title>{`ชนะ ${wins} · แพ้ ${losses} (${pct(n ? wins / n : null)})`}</title>
      <circle cx={c} cy={c} r={r} fill="none" stroke={TRACK} strokeWidth={thickness} />
      {n > 0 && wins === n ? <circle cx={c} cy={c} r={r} fill="none" stroke={WIN} strokeWidth={thickness} /> : null}
      {n > 0 && losses === n ? <circle cx={c} cy={c} r={r} fill="none" stroke={LOSS} strokeWidth={thickness} /> : null}
      {wins > 0 && losses > 0 ? (
        <>
          <path d={arc(c, c, r, gap / 2, deg - gap / 2)} fill="none" stroke={WIN} strokeWidth={thickness} strokeLinecap="round" />
          <path d={arc(c, c, r, deg + gap / 2, 360 - gap / 2)} fill="none" stroke={LOSS} strokeWidth={thickness} strokeLinecap="round" opacity={0.85} />
        </>
      ) : null}
      {center ? (
        <text x={c} y={c + 4} textAnchor="middle" fontSize={size * 0.2} fontWeight={700} fill="var(--text)" fontFamily="var(--font-display)">
          {center}
        </text>
      ) : null}
    </svg>
  );
}

/** Semicircle gauge filled to `value` (0–1) with a tick at `mark` (e.g. the team average). */
export function HalfGauge({ value, mark, color = "var(--accent)", width = 92, thickness = 9, n }) {
  const r = (width - thickness) / 2;
  const cx = width / 2;
  const cy = r + thickness / 2;
  const v = Math.max(0, Math.min(1, value ?? 0));
  const end = -90 + 180 * v;
  const [mx1, my1] = mark == null ? [0, 0] : polar(cx, cy, r - thickness / 2 - 2, -90 + 180 * mark);
  const [mx2, my2] = mark == null ? [0, 0] : polar(cx, cy, r + thickness / 2 + 2, -90 + 180 * mark);
  return (
    <svg width={width} height={cy + 4} role="img" aria-label={`อัตราชนะ ${pct(value)}`}>
      <title>{`อัตราชนะ ${pct(value)}${n != null ? ` · ${n} เกม` : ""}${mark != null ? ` · เฉลี่ยทีม ${pct(mark)}` : ""}`}</title>
      <path d={arc(cx, cy, r, -90, 90)} fill="none" stroke={TRACK} strokeWidth={thickness} strokeLinecap="round" />
      {v > 0 ? <path d={arc(cx, cy, r, -90, Math.max(-89.5, end))} fill="none" stroke={color} strokeWidth={thickness} strokeLinecap="round" /> : null}
      {mark != null ? <line x1={mx1} y1={my1} x2={mx2} y2={my2} stroke="var(--text)" strokeWidth={2} strokeLinecap="round" /> : null}
    </svg>
  );
}

/** Stacked win / loss columns, e.g. games per week. items: [{ label, n, w }] */
export function SparkBars({ items, height = 46 }) {
  const [ref, width] = useWidth();
  const max = Math.max(1, ...items.map((d) => d.n));
  const gap = items.length > 24 ? 1 : 2;
  const bw = items.length ? Math.max(2, (width - gap * (items.length - 1)) / items.length) : 0;
  return (
    <div ref={ref} style={{ height }}>
      {width ? (
        <svg width={width} height={height} role="img" aria-label="จำนวนเกมแต่ละช่วง">
          {items.map((d, i) => {
            const x = i * (bw + gap);
            const hw = (d.w / max) * (height - 2);
            const hl = ((d.n - d.w) / max) * (height - 2);
            return (
              <g key={d.label}>
                <title>{`${d.label}: ${d.n} เกม · ชนะ ${d.w} แพ้ ${d.n - d.w} (${pct(d.n ? d.w / d.n : null, 0)})`}</title>
                <rect x={x} y={0} width={bw} height={height} fill="transparent" />
                {hl > 0 ? <rect x={x} y={height - hw - hl} width={bw} height={Math.max(1, hl - (hw > 0 ? 1 : 0))} rx={1.5} fill={LOSS} opacity={0.85} /> : null}
                {hw > 0 ? <rect x={x} y={height - hw} width={bw} height={hw} rx={1.5} fill={WIN} /> : null}
              </g>
            );
          })}
        </svg>
      ) : null}
    </div>
  );
}

/** Line of rates (0–1) with dots, gaps for null, and a dashed reference line. points: [{ label, value, n, w }] */
export function Sparkline({ points, reference, height = 46, color = "var(--accent)" }) {
  const [ref, width] = useWidth();
  const pad = 4;
  const xs = (i) => pad + (points.length > 1 ? (i / (points.length - 1)) * (width - pad * 2) : (width - pad * 2) / 2);
  const ys = (v) => pad + (1 - v) * (height - pad * 2);
  const segs = [];
  let cur = [];
  points.forEach((p, i) => {
    if (p.value == null) {
      if (cur.length) segs.push(cur);
      cur = [];
    } else cur.push([xs(i), ys(p.value)]);
  });
  if (cur.length) segs.push(cur);
  return (
    <div ref={ref} style={{ height }}>
      {width ? (
        <svg width={width} height={height} role="img" aria-label="แนวโน้มอัตราชนะ">
          <line x1={pad} x2={width - pad} y1={ys(0.5)} y2={ys(0.5)} stroke="rgba(255,255,255,0.06)" />
          {reference != null ? <line x1={pad} x2={width - pad} y1={ys(reference)} y2={ys(reference)} stroke="var(--text-3)" strokeDasharray="3 3" /> : null}
          {segs.map((s, k) => (
            <polyline key={k} points={s.map((p) => p.join(",")).join(" ")} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          ))}
          {points.map((p, i) =>
            p.value == null ? (
              <g key={i}>
                <title>{`${p.label}: ไม่มีเกม`}</title>
                <circle cx={xs(i)} cy={height - pad} r={2} fill="var(--text-4)" />
              </g>
            ) : (
              <g key={i}>
                <title>{`${p.label}: ${pct(p.value, 0)} (${p.w}–${p.n - p.w}, ${p.n} เกม)`}</title>
                <circle cx={xs(i)} cy={ys(p.value)} r={i === points.length - 1 ? 4 : 3} fill={p.value >= (reference ?? 0.5) ? WIN : LOSS} stroke="var(--surface)" strokeWidth={1.5} />
              </g>
            )
          )}
        </svg>
      ) : null}
    </div>
  );
}

/** Histogram with stacked wins / losses and an optional marker. bins: [{ label, from, to, w, l }] */
export function MiniHistogram({ bins, marker, markerLabel, height = 46 }) {
  const [ref, width] = useWidth();
  const max = Math.max(1, ...bins.map((b) => b.w + b.l));
  const gap = 2;
  const bw = bins.length ? (width - gap * (bins.length - 1)) / bins.length : 0;
  const lo = bins.length ? bins[0].from : 0;
  const hi = bins.length ? bins[bins.length - 1].to : 1;
  const mx = marker == null ? null : ((marker - lo) / Math.max(0.001, hi - lo)) * width;
  return (
    <div ref={ref} style={{ height }}>
      {width ? (
        <svg width={width} height={height} role="img" aria-label="การกระจายความยาวเกม">
          {bins.map((b, i) => {
            const x = i * (bw + gap);
            const hw = (b.w / max) * (height - 2);
            const hl = (b.l / max) * (height - 2);
            return (
              <g key={b.label}>
                <title>{`${b.label} นาที: ${b.w + b.l} เกม · ชนะ ${b.w} แพ้ ${b.l}`}</title>
                <rect x={x} y={0} width={bw} height={height} fill="transparent" />
                {hl > 0 ? <rect x={x} y={height - hw - hl} width={bw} height={Math.max(1, hl - (hw > 0 ? 1 : 0))} rx={1.5} fill={LOSS} opacity={0.85} /> : null}
                {hw > 0 ? <rect x={x} y={height - hw} width={bw} height={hw} rx={1.5} fill={WIN} /> : null}
              </g>
            );
          })}
          {mx != null ? (
            <g>
              <title>{markerLabel}</title>
              <line x1={mx} x2={mx} y1={0} y2={height} stroke="var(--accent)" strokeWidth={2} />
            </g>
          ) : null}
        </svg>
      ) : null}
    </div>
  );
}

/** Consecutive results as runs: win streaks grow up (green), losing streaks down (red). runs: [{ win, len, title }] */
export function StreakRuns({ runs, height = 52 }) {
  const [ref, width] = useWidth();
  const max = Math.max(1, ...runs.map((r) => r.len));
  const gap = 3;
  const bw = runs.length ? (width - gap * (runs.length - 1)) / runs.length : 0;
  const mid = height / 2;
  return (
    <div ref={ref} style={{ height }}>
      {width ? (
        <svg width={width} height={height} role="img" aria-label="สตรีคชนะแพ้ย้อนหลัง">
          <line x1={0} x2={width} y1={mid} y2={mid} stroke="rgba(255,255,255,0.12)" />
          {runs.map((r, i) => {
            const h = Math.max(2, (r.len / max) * (mid - 2));
            const x = i * (bw + gap);
            const last = i === runs.length - 1;
            return (
              <g key={i}>
                <title>{r.title}</title>
                <rect x={x} y={0} width={bw} height={height} fill="transparent" />
                <rect x={x} y={r.win ? mid - h : mid + 1} width={bw} height={h} rx={2} fill={r.win ? WIN : LOSS} opacity={last ? 1 : 0.55} />
              </g>
            );
          })}
        </svg>
      ) : null}
    </div>
  );
}

/** A few labelled win / loss bars. rows: [{ label, n, w }] */
export function BarList({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div style={{ display: "grid", gap: 5 }}>
      {rows.map((r) => (
        <div
          key={r.label}
          style={{ display: "grid", gridTemplateColumns: "44px 1fr 34px", alignItems: "center", gap: 8, fontSize: 11.5 }}
          title={`${r.label}: ${r.n} เกม · ชนะ ${r.w} แพ้ ${r.n - r.w} (${pct(r.w / r.n)})`}
        >
          <span className="ds-truncate" style={{ color: "var(--text-2)", fontWeight: 600 }}>
            {r.label}
          </span>
          <span style={{ display: "flex", height: 6, width: `${(r.n / max) * 100}%`, gap: 2 }}>
            {r.w ? <i style={{ flex: r.w, background: WIN, borderRadius: 3 }} /> : null}
            {r.n - r.w ? <i style={{ flex: r.n - r.w, background: LOSS, borderRadius: 3, opacity: 0.85 }} /> : null}
          </span>
          <span className="tnum" style={{ color: "var(--text-3)", textAlign: "right" }}>
            {r.n}
          </span>
        </div>
      ))}
    </div>
  );
}
